import React, { useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { Box, Stack } from '@mui/material'
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@material-ui/icons'
import { parse } from 'papaparse'
import { badgesSubjectsApplication } from 'constants/index'
import {
  bulkCreateBadgeSubjectsFromCSV,
  getExistingCountersForType,
  getHighestBadgeCounter
} from 'store/badges'
import ProgressButton from 'components/ProgressButton'
import { downloadBadgeSubjectSampleCSV } from 'utils/downloadCSV'
import { isValidEmail, parseCsvData } from 'utils'
import { useStyles } from 'views/admin/BadgesV1/ImportBadgesViaCSV/styles'

const ImportBadgesCSVModal = ({ open, onClose, conferenceId }) => {
  const classes = useStyles()
  const csvInputRef = useRef()

  const [showInstructions, setShowInstructions] = useState(true)
  const [csvSubjectType, setCsvSubjectType] = useState('')
  const [csvTypeError, setCsvTypeError] = useState('')
  const [csvFile, setCsvFile] = useState(null)
  const [csvError, setCsvError] = useState('')
  const [csvParsedRows, setCsvParsedRows] = useState([])
  const [invalidCount, setInvalidCount] = useState(0)
  const [showCsvReview, setShowCsvReview] = useState(false)
  const [importing, setImporting] = useState(false)

  // Counter conflict state
  const [conflictedCounters, setConflictedCounters] = useState(new Set())
  const [counterConflictDecision, setCounterConflictDecision] = useState(null) // null | 'yes' | 'no'
  const [checkingConflicts, setCheckingConflicts] = useState(false)

  const getRowErrors = (row) => {
    const errors = new Set()
    if (!row.counter) errors.add('counter')
    if (!row.firstName) errors.add('firstName')
    if (!row.lastName) errors.add('lastName')
    if (!row.email || !isValidEmail(row.email)) errors.add('email')
    return errors
  }

  const resetState = () => {
    setCsvSubjectType('')
    setCsvTypeError('')
    setCsvFile(null)
    setCsvError('')
    setCsvParsedRows([])
    setInvalidCount(0)
    setShowCsvReview(false)
    setConflictedCounters(new Set())
    setCounterConflictDecision(null)
    setCheckingConflicts(false)
    if (csvInputRef.current) csvInputRef.current.value = null
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleTypeSelect = (type) => {
    if (type === csvSubjectType) return
    setCsvSubjectType(type)
    setCsvTypeError('')
    setCsvFile(null)
    setCsvError('')
    setCsvParsedRows([])
    setInvalidCount(0)
    setShowCsvReview(false)
    setConflictedCounters(new Set())
    setCounterConflictDecision(null)
    setCheckingConflicts(false)
    if (csvInputRef.current) csvInputRef.current.value = null
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      setCsvError('Invalid file type. Please upload a .csv file.')
      setCsvParsedRows([])
      setInvalidCount(0)
      setShowCsvReview(false)
      setConflictedCounters(new Set())
      setCounterConflictDecision(null)
      event.target.value = null
      return
    }

    setCsvFile(file)
    setCsvError('')
    setCsvParsedRows([])
    setInvalidCount(0)
    setShowCsvReview(false)
    setConflictedCounters(new Set())
    setCounterConflictDecision(null)

    parse(file, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: async (results) => {
        const rows = parseCsvData(results.data)

        if (!rows.length) {
          setCsvError('No rows found in the CSV file.')
          return
        }

        const badCount = rows.filter((r) => getRowErrors(r).size > 0).length
        setInvalidCount(badCount)
        setCsvParsedRows(rows)
        setShowCsvReview(true)

        // Check for counter conflicts in Firestore — only for rows that will actually be imported
        const validRows = rows.filter((r) => getRowErrors(r).size === 0)
        const csvCounters = validRows.map((r) => parseInt(r.counter, 10)).filter((n) => !isNaN(n))

        if (csvCounters.length > 0) {
          setCheckingConflicts(true)
          const taken = await getExistingCountersForType(conferenceId, csvSubjectType)
          const conflicts = new Set(csvCounters.filter((c) => taken.has(c)))
          setConflictedCounters(conflicts)
          setCheckingConflicts(false)
        }
      },
      error: (err) => {
        setCsvError(err.message || 'Failed to parse CSV file.')
      }
    })

    event.target.value = null
  }

  const handleImport = async () => {
    if (!csvSubjectType) {
      setCsvTypeError('Please select a subject type before importing.')
      return
    }
    if (!csvParsedRows.length || !showCsvReview) {
      setCsvError('Please select and review a CSV file before importing.')
      return
    }

    const validRows = csvParsedRows.filter((r) => getRowErrors(r).size === 0)

    setImporting(true)
    try {
      if (conflictedCounters.size > 0 && counterConflictDecision === 'no') {
        // Separate valid rows into non-conflicted (keep counter) and conflicted (reassign counter)
        const nonConflicted = validRows.filter(
          (r) => !conflictedCounters.has(parseInt(r.counter, 10))
        )
        const conflictedRows = validRows.filter((r) =>
          conflictedCounters.has(parseInt(r.counter, 10))
        )

        // Import non-conflicted rows first (they keep their original counters)
        if (nonConflicted.length > 0) {
          await bulkCreateBadgeSubjectsFromCSV(nonConflicted, conferenceId, csvSubjectType)
        }

        // Now fetch the true highest counter (after the first batch is committed) and
        // pre-assign sequential counters to conflicted rows before the second write
        if (conflictedRows.length > 0) {
          const highestBadge = await getHighestBadgeCounter(conferenceId, csvSubjectType)
          let nextCounter = highestBadge ? highestBadge.counter + 1 : 1
          const reassigned = conflictedRows.map((r) => ({
            ...r,
            counter: String(nextCounter++)
          }))
          await bulkCreateBadgeSubjectsFromCSV(reassigned, conferenceId, csvSubjectType)
        }
      } else {
        // Yes (keep same counters) or no conflicts at all
        await bulkCreateBadgeSubjectsFromCSV(validRows, conferenceId, csvSubjectType)
      }
      handleClose()
    } catch {
      // toast.error already shown inside bulkCreateBadgeSubjectsFromCSV
    } finally {
      setImporting(false)
    }
  }

  const hasConflicts = conflictedCounters.size > 0
  const conflictDecisionNeeded = hasConflicts && counterConflictDecision === null
  const importDisabled =
    importing || (csvSubjectType && (!showCsvReview || checkingConflicts || conflictDecisionNeeded))

  return (
    <Dialog open={open} maxWidth="xl" fullWidth classes={{ paper: classes.dialogPaper }}>
      {/* Header */}
      <Box className={classes.dialogTitle}>
        <Typography variant="h5">Import Badge Subjects from CSV</Typography>
        {!importing && (
          <IconButton onClick={handleClose} size="small" className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <DialogContent className={classes.dialogContent}>
        {/* Instructions panel */}
        <Box className={classes.note}>
          <Box className={classes.noteHeader} onClick={() => setShowInstructions((v) => !v)}>
            <Typography className={classes.noteHead}>
              Instructions to Import Badge Subjects:
            </Typography>
            {showInstructions ? (
              <ExpandLessIcon className={classes.accordionIcon} />
            ) : (
              <ExpandMoreIcon className={classes.accordionIcon} />
            )}
          </Box>

          {showInstructions && (
            <>
              <Box className={classes.noteSteps}>
                {/* Step 1 */}
                <Box className={classes.stepItem}>
                  <Box className={classes.stepNumber}>1</Box>
                  <Typography className={classes.stepText}>
                    Select the subject type below (e.g. Attendee, Speaker).
                  </Typography>
                </Box>
                {/* Step 2 */}
                <Box className={classes.stepItem}>
                  <Box className={classes.stepNumber}>2</Box>
                  <Typography className={classes.stepText}>
                    Prepare a CSV file according to the columns listed below, then select it from
                    your computer.
                  </Typography>
                </Box>
                {/* Step 3 */}
                <Box className={classes.stepItem}>
                  <Box className={classes.stepNumber}>3</Box>
                  <Typography className={classes.stepText}>
                    {`Review the data and click 'Import Subjects' to bulk-add all badge subjects.`}
                  </Typography>
                </Box>
              </Box>

              {/* Column format table */}
              <Box className={classes.fileFormatInfo}>
                <Box>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '1rem'
                    }}>
                    <Typography variant="subtitle2" className={classes.formatTitle}>
                      Sample CSV Format
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      className={classes.sampleDownloadButton}
                      onClick={downloadBadgeSubjectSampleCSV}
                      startIcon={<DownloadIcon />}>
                      Download Sample CSV
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.formatHeaderCell}>
                            Counter <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>
                            First Name <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>
                            Last Name <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>
                            Email <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>Credential</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Position</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Organization</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Phone</TableCell>
                          <TableCell className={classes.formatHeaderCell}>City</TableCell>
                          <TableCell className={classes.formatHeaderCell}>State</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Country</TableCell>
                        </TableRow>
                      </TableHead>
                    </Table>
                  </TableContainer>

                  <Box className={classes.roleInfoBox}>
                    <InfoIcon className={classes.roleInfoIcon} />
                    <Typography className={classes.roleInfoText}>
                      Fields marked <strong>*</strong> are required. <strong>Counter</strong> must
                      be a unique number per subject type. The subject type is taken from your
                      selection in Step 1 — no need to include it in the CSV.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* Step 1: Subject type radio cards */}
        <Box mb={2}>
          <Typography className={classes.sectionLabel}>Step 1 — Select Subject Type</Typography>
          <Stack direction="row" flexWrap="wrap" gap="8px" mt={1}>
            {badgesSubjectsApplication.map((type) => {
              const selected = csvSubjectType === type
              return (
                <Box
                  key={type}
                  className={`${classes.radioCard} ${selected ? classes.radioCardSelected : ''}`}
                  onClick={() => handleTypeSelect(type)}>
                  <Radio
                    checked={selected}
                    color="primary"
                    size="small"
                    style={{ padding: 0 }}
                    onChange={() => handleTypeSelect(type)}
                  />
                  <span className={classes.radioCardLabel}>{type}</span>
                </Box>
              )
            })}
          </Stack>
          {csvTypeError && (
            <Typography variant="caption" color="error" display="block" style={{ marginTop: 6 }}>
              {csvTypeError}
            </Typography>
          )}
        </Box>

        {/* Step 2: File upload */}
        <Box mt={3}>
          <input
            type="file"
            accept=".csv"
            ref={csvInputRef}
            className={classes.input}
            onChange={handleFileChange}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              component="span"
              className={classes.upload}
              startIcon={<CloudUploadIcon />}
              disabled={!csvSubjectType}
              onClick={() => csvInputRef.current && csvInputRef.current.click()}>
              {csvFile ? csvFile.name : 'Select CSV file'}
            </Button>
            {csvFile && (
              <IconButton
                size="small"
                title="Clear selected file"
                onClick={() => {
                  setCsvFile(null)
                  setCsvError('')
                  setCsvParsedRows([])
                  setInvalidCount(0)
                  setShowCsvReview(false)
                  setConflictedCounters(new Set())
                  setCounterConflictDecision(null)
                  if (csvInputRef.current) csvInputRef.current.value = null
                }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
          {!csvSubjectType && (
            <Typography className={classes.helperText} style={{ marginTop: 6 }}>
              Select a subject type above to enable file upload.
            </Typography>
          )}
          {csvError && <Typography className={classes.error}>{csvError}</Typography>}
        </Box>

        {/* Step 3: Review table */}
        {showCsvReview && csvParsedRows.length > 0 && (
          <Box className={classes.tableContainer}>
            <Typography className={classes.tableTitle}>
              Review Badge Subjects ({csvParsedRows.length})
            </Typography>

            {/* Validation errors banner */}
            {invalidCount > 0 && (
              <Box className={classes.warningBanner}>
                <InfoIcon className={classes.warningIcon} />
                <Typography className={classes.warningText}>
                  <strong>
                    {invalidCount} row{invalidCount !== 1 ? 's' : ''} have invalid or missing
                    required fields
                  </strong>{' '}
                  (highlighted in red below). These rows will be <strong>skipped</strong> during
                  import — only valid rows will be imported. You can also fix the CSV and re-upload.
                  Required fields: <strong>Counter</strong>, <strong>First Name</strong>,{' '}
                  <strong>Last Name</strong>, <strong>Email</strong>.
                </Typography>
              </Box>
            )}

            {/* Counter conflict banner */}
            {checkingConflicts && (
              <Box className={classes.importInfo}>
                <Box className={classes.infoNumberCircle}>i</Box>
                <Typography className={classes.importInfoText}>
                  Checking for counter conflicts…
                </Typography>
              </Box>
            )}

            {!checkingConflicts && hasConflicts && (
              <Box className={classes.conflictBanner}>
                <WarningIcon className={classes.conflictIcon} />
                <Box flex={1}>
                  <Typography className={classes.conflictText}>
                    <strong>
                      {conflictedCounters.size} counter
                      {conflictedCounters.size !== 1 ? 's' : ''} already exist
                    </strong>{' '}
                    in the system for type <strong>{csvSubjectType}</strong> (highlighted in orange
                    below):{' '}
                    <strong>{[...conflictedCounters].sort((a, b) => a - b).join(', ')}</strong>.
                  </Typography>
                  {counterConflictDecision === null && (
                    <>
                      <Typography className={classes.conflictQuestion}>
                        How would you like to handle these records?
                      </Typography>
                      <Stack direction="row" spacing={1} mt={1}>
                        <Button
                          variant="contained"
                          size="small"
                          className={classes.conflictYesBtn}
                          onClick={() => setCounterConflictDecision('yes')}>
                          Yes — keep same counters
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          className={classes.conflictNoBtn}
                          onClick={() => setCounterConflictDecision('no')}>
                          No — assign new counters
                        </Button>
                      </Stack>
                      <Box mt={1}>
                        <Typography className={classes.conflictHint}>
                          <strong>Yes</strong> — imports all records as-is, including duplicates.
                        </Typography>
                        <Typography className={classes.conflictHint}>
                          <strong>No</strong> — non-conflicted records keep their counters;
                          conflicted records are reassigned starting from the next available
                          counter.
                        </Typography>
                      </Box>
                    </>
                  )}
                  {counterConflictDecision === 'yes' && (
                    <Typography className={classes.conflictDecisionNote}>
                      Duplicate counters will be kept.{' '}
                      <Button
                        size="small"
                        className={classes.changeDecisionBtn}
                        onClick={() => setCounterConflictDecision(null)}>
                        Change
                      </Button>
                    </Typography>
                  )}
                  {counterConflictDecision === 'no' && (
                    <Typography className={classes.conflictDecisionNote}>
                      Conflicted records will be reassigned new counters.{' '}
                      <Button
                        size="small"
                        className={classes.changeDecisionBtn}
                        onClick={() => setCounterConflictDecision(null)}>
                        Change
                      </Button>
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* All good banner */}
            {!checkingConflicts && !hasConflicts && invalidCount === 0 && (
              <Box className={classes.importInfo}>
                <Box className={classes.infoNumberCircle}>i</Box>
                <Typography className={classes.importInfoText}>
                  All {csvParsedRows.length} row{csvParsedRows.length !== 1 ? 's' : ''} look good.
                  Click <strong>Import Subjects</strong> to proceed.
                </Typography>
              </Box>
            )}

            <TableContainer component={Paper} className={classes.reviewTable}>
              <Table size="small">
                <TableHead className={classes.reviewHeader}>
                  <TableRow>
                    <TableCell className={classes.reviewHeaderCell}>
                      Counter <span className={classes.requiredMark}>*</span>
                    </TableCell>
                    <TableCell className={classes.reviewHeaderCell}>
                      First Name <span className={classes.requiredMark}>*</span>
                    </TableCell>
                    <TableCell className={classes.reviewHeaderCell}>
                      Last Name <span className={classes.requiredMark}>*</span>
                    </TableCell>
                    <TableCell className={classes.reviewHeaderCell}>
                      Email <span className={classes.requiredMark}>*</span>
                    </TableCell>
                    <TableCell className={classes.reviewHeaderCell}>Credential</TableCell>
                    <TableCell className={classes.reviewHeaderCell}>Position</TableCell>
                    <TableCell className={classes.reviewHeaderCell}>Organization</TableCell>
                    <TableCell className={classes.reviewHeaderCell}>City</TableCell>
                    <TableCell className={classes.reviewHeaderCell}>State</TableCell>
                    <TableCell className={classes.reviewHeaderCell}>Country</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvParsedRows.map((row, index) => {
                    const errors = getRowErrors(row)
                    const rowInvalid = errors.size > 0
                    const counterNum = parseInt(row.counter, 10)
                    const rowConflict = !isNaN(counterNum) && conflictedCounters.has(counterNum)
                    const borderColor = rowInvalid
                      ? '#f44336'
                      : rowConflict
                      ? '#ff9800'
                      : 'transparent'
                    return (
                      <TableRow
                        key={index}
                        style={
                          rowInvalid || rowConflict
                            ? { borderLeft: `3px solid ${borderColor}` }
                            : {}
                        }>
                        <TableCell
                          className={`${classes.reviewCell} ${
                            errors.has('counter')
                              ? classes.cellError
                              : rowConflict
                              ? classes.cellConflict
                              : ''
                          }`}>
                          {row.counter || <span className={classes.missingValue}>Missing</span>}
                        </TableCell>
                        <TableCell
                          className={`${classes.reviewCell} ${
                            errors.has('firstName') ? classes.cellError : ''
                          }`}>
                          {row.firstName || <span className={classes.missingValue}>Missing</span>}
                        </TableCell>
                        <TableCell
                          className={`${classes.reviewCell} ${
                            errors.has('lastName') ? classes.cellError : ''
                          }`}>
                          {row.lastName || <span className={classes.missingValue}>Missing</span>}
                        </TableCell>
                        <TableCell
                          className={`${classes.reviewCell} ${
                            errors.has('email') ? classes.cellError : ''
                          }`}>
                          {row.email || <span className={classes.missingValue}>Missing</span>}
                        </TableCell>
                        <TableCell className={classes.reviewCell}>
                          {row.credential || '-'}
                        </TableCell>
                        <TableCell className={classes.reviewCell}>{row.position || '-'}</TableCell>
                        <TableCell className={classes.reviewCell}>
                          {row.organization || '-'}
                        </TableCell>
                        <TableCell className={classes.reviewCell}>{row.city || '-'}</TableCell>
                        <TableCell className={classes.reviewCell}>{row.state || '-'}</TableCell>
                        <TableCell className={classes.reviewCell}>{row.country || '-'}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      {/* Footer actions */}
      <Box className={classes.buttonBox}>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>
        <ProgressButton
          label="Import Subjects"
          onClick={handleImport}
          disabled={importDisabled}
          inProgress={importing}
          variant="contained"
          color="primary"
        />
      </Box>
    </Dialog>
  )
}

export default ImportBadgesCSVModal
